import React, { useEffect, useState } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import PreviewMenuView from "@/components/menu/PreviewMenuView";
import { loadCustomerExperience } from "@/lib/customerExperience";
import { Eye, Monitor, Smartphone } from "lucide-react";

const getBusinessSubDomain = (user = {}) =>
  user.businessSubDomain ??
  user.BusinessSubDomain ??
  user.subDomain ??
  user.SubDomain ??
  "";

const getBusinessName = (user = {}) =>
  user.businessName ||
  user.business_name ||
  user.businessName ||
  user.name ||
  user.business?.name ||
  "";

const getBusinessLogo = (user = {}) =>
  user.logo_url ||
  user.logoUrl ||
  user.businessLogo ||
  user.business?.logo_url ||
  user.business?.logoUrl ||
  "";

const normalizePromotions = (promotions = []) =>
  promotions.map((promotion) => {
    const rawProducts = promotion.products ?? promotion.Products ?? [];

    const selectedProductIds =
      promotion.productIds ??
      promotion.ProductIds ??
      promotion.selectedProductIds ??
      promotion.SelectedProductIds ??
      rawProducts.map((product) => product.id ?? product.Id) ??
      [];

    return {
      id: promotion.id ?? promotion.Id,
      title: promotion.name ?? promotion.Name ?? "",
      description: promotion.description ?? promotion.Description ?? "",
      image_url:
        promotion.image_url ??
        promotion.imageUrl ??
        promotion.ImageUrl ??
        promotion.imageFile?.fileUrl ??
        promotion.ImageFile?.fileUrl ??
        "",
      imageId: promotion.imageId ?? promotion.ImageId ?? null,
      is_active: promotion.visible ?? promotion.Visible ?? true,
      selectedProductIds: Array.isArray(selectedProductIds)
        ? selectedProductIds.map((id) => String(id))
        : [],
      startDate: promotion.beginDate ?? promotion.BeginDate ?? "",
      endDate: promotion.endDate ?? promotion.EndDate ?? "",
      order:
        promotion.sortOrder ??
        promotion.SortOrder ??
        promotion.index ??
        promotion.Index ??
        0,
      raw: promotion,
    };
  });

export default function PreviewPage() {
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);

  const [businessInfo, setBusinessInfo] = useState({
    name: "",
    subDomain: "",
    logoUrl: "",
  });

  const [customerPopupData, setCustomerPopupData] = useState(null);

  useEffect(() => {
    const loadPreview = async () => {
      try {
        setLoading(true);

        const rawUser = localStorage.getItem("user");

        if (!rawUser) {
          toast.error("Kullanıcı bilgisi bulunamadı.");
          return;
        }

        const user = JSON.parse(rawUser);
        const businessSubDomain = getBusinessSubDomain(user);

        if (!businessSubDomain) {
          toast.error("İşletme alan adı bulunamadı.");
          return;
        }

        const businessRes = await api.getBusinessBySubDomain(
          businessSubDomain
        );

        const business = businessRes?.data || {};

        setBusinessInfo({
          name: business.name || getBusinessName(user),
          subDomain: business.subDomain || businessSubDomain,
          logoUrl: business.logo_url || getBusinessLogo(user),
        });

        setCustomerPopupData(loadCustomerExperience(businessSubDomain));

        const [promotionsRes, categoriesRes, productsRes] = await Promise.all([
          api.getPublicPromotions({
            businessSubDomain,
            page: 0,
            pageSize: 1000,
          }),

          api.getCategories({
            businessSubDomain,
            visible: true,
            page: 0,
            pageSize: 1000,
          }),

          api.getMenuItems({
            businessSubDomain,
            status: true,
            page: 0,
            pageSize: 1000,
          }),
        ]);

        setBanners(normalizePromotions(promotionsRes?.data || []));
        setCategories(categoriesRes?.data || []);
        setMenuItems(productsRes?.data || []);
      } catch (error) {
        console.error("Preview yüklenirken hata:", error);
        toast.error("Menü önizlemesi yüklenirken bir hata oluştu.");
      } finally {
        setLoading(false);
      }
    };

    loadPreview();
  }, []);

  return (
    <AdminLayout>
      <div className="mx-auto w-full max-w-[1240px] space-y-0 md:space-y-5">
        <section className="relative hidden overflow-hidden rounded-[30px] border border-zinc-200/80 bg-white p-7 shadow-[0_22px_65px_rgba(24,24,27,0.05)] md:block">
          <div
            aria-hidden="true"
            className="absolute -right-16 -top-20 h-64 w-64 rounded-full bg-orange-100/80 blur-3xl"
          />

          <div className="relative flex items-start justify-between gap-8">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3.5 py-2 text-[11px] font-extrabold uppercase tracking-[0.13em] text-orange-700">
                <Eye className="h-3.5 w-3.5" />
                Vitrin Ön İzleme
              </div>

              <h1 className="mt-5 text-[34px] font-extrabold tracking-[-0.04em] text-zinc-950 lg:text-[40px]">
                Müşterilerinizin gördüğü deneyimi kontrol edin.
              </h1>

              <p className="mt-3 max-w-xl text-sm font-medium leading-6 text-zinc-500">
                Yayındaki kategorileri, içerikleri ve kampanyaları Web Menü
                vitrininizde nasıl göründükleriyle birlikte inceleyin.
              </p>
            </div>

            <div className="grid min-w-[255px] gap-3">
              <div className="rounded-[20px] border border-zinc-200 bg-[#FAFAF9] p-4">
                <p className="text-[10px] font-extrabold uppercase tracking-[0.13em] text-zinc-400">
                  İşletme
                </p>
                <p className="mt-1.5 truncate text-sm font-extrabold text-zinc-900">
                  {businessInfo.name || "İşletmeniz"}
                </p>
              </div>

              <div className="flex items-center gap-3 rounded-[20px] border border-orange-100 bg-orange-50/60 p-4">
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[13px] bg-white text-[#EA580C] shadow-sm">
                  <Smartphone className="h-[18px] w-[18px]" />
                </div>

                <div className="min-w-0">
                  <p className="text-[10px] font-extrabold uppercase tracking-[0.12em] text-orange-700">
                    Web Menü Adresi
                  </p>
                  <p className="mt-1 truncate text-xs font-bold text-zinc-700">
                    {businessInfo.subDomain
                      ? `webmenu.megriva.com/${businessInfo.subDomain}`
                      : "Hazırlanıyor"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="overflow-hidden md:rounded-[30px] md:border md:border-zinc-200/80 md:bg-white md:shadow-[0_18px_55px_rgba(24,24,27,0.04)]">
          <div className="hidden h-14 items-center justify-between border-b border-zinc-100 px-5 md:flex">
            <div className="flex items-center gap-3">
              <div className="flex gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-zinc-200" />
                <span className="h-2.5 w-2.5 rounded-full bg-zinc-200" />
                <span className="h-2.5 w-2.5 rounded-full bg-zinc-200" />
              </div>

              <div className="h-5 w-px bg-zinc-200" />

              <div className="flex items-center gap-2 text-xs font-bold text-zinc-500">
                <Monitor className="h-4 w-4 text-zinc-400" />
                Müşteri görünümü
              </div>
            </div>

            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.11em] text-emerald-700">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Canlı Ön İzleme
            </div>
          </div>

          <div className="md:bg-[#F7F7F5] md:p-4 lg:p-6">
            <div className="overflow-hidden md:rounded-[24px] md:border md:border-zinc-200/80 md:bg-white">
              <PreviewMenuView
                categories={categories}
                menuItems={menuItems}
                banners={banners}
                businessInfo={businessInfo}
                customerPopupData={customerPopupData}
                loading={loading}
                loadingText="Önizleme yükleniyor..."
                audience="admin"
              />
            </div>
          </div>
        </section>
      </div>
    </AdminLayout>
  );
}